import { AudioEngine } from '@/audio/AudioEngine'
import { sfxUi } from '@/audio/sfx'
import {
  AbilityId,
  Buff,
  Instability,
  Transform,
  Velocity,
  type CharacterKind,
  type PickupKind,
  type PlayerId,
} from '@/components'
import type { EventBus } from '@/core/EventBus'
import { Stage, type Scheduler, type SystemContext } from '@/core/ecs/Scheduler'
import type { World } from '@/core/ecs/World'
import { Time } from '@/core/Time'
import { ArenaResource } from '@/game/arenas/Arena'
import { ARENAS, arenaById, scaledArena } from '@/game/arenas'
import { HitStop } from '@/game/hitStop'
import {
  DEFAULT_SETTINGS,
  MatchResource,
  ViewerResource,
  teamName,
  type Settings,
} from '@/game/resources'
import { createSimulation, stepSimulation, type Simulation } from '@/game/simulation'
import { Renderer } from '@/gfx/Renderer'
import { loadFighterModels } from '@/gfx/assets/fighterModels'
import { CHARACTER_KINDS } from '@/gfx/procedural/characterKinds'
import { SceneBridgeResource } from '@/gfx/SceneBridge'
import {
  InputManager,
  InputResource,
  consumePresses,
  createInputFrame,
  resetInput,
} from '@/input/InputManager'
import type { NetClient } from '@/net/NetClient'
import { OnlinePlay } from '@/net/OnlinePlay'
import { RoomAccess } from '@/ui/RoomAccess'
import { createAudioSystem } from '@/systems/AudioSystem'
import { createCameraSystem } from '@/systems/CameraSystem'
import { createHudSystem } from '@/systems/HudSystem'
import { createInputPollSystem } from '@/systems/InputSystem'
import { spawnPickup } from '@/systems/PickupSystem'
import { createRenderSyncSystem } from '@/systems/RenderSyncSystem'
import { clearRound, startMatch, startRound } from '@/systems/RoundSystem'
import { createVfxSystem } from '@/systems/VfxSystem'
import { Hud, el } from '@/ui/Hud'
import { Menu, type MatchConfig } from '@/ui/Menu'

/**
 * Wires the whole game together and owns the frame loop.
 *
 * The loop is the textbook fixed-timestep one: simulate in whole 60 Hz steps,
 * render once with the leftover fraction as an interpolation factor. Everything
 * else in this class is lifecycle — menus, pausing, settings, and the single
 * user gesture that browsers require before audio may start.
 */
export class Game {
  private readonly sim: Simulation
  private readonly world: World
  private readonly scheduler: Scheduler
  private readonly events: EventBus
  private readonly time = new Time()
  private readonly input = new InputManager()
  private readonly renderer: Renderer
  private readonly audio = new AudioEngine()
  private readonly hud: Hud
  private readonly menu: Menu
  private readonly rooms: RoomAccess
  private readonly debugEl: HTMLElement

  private settings: Settings = { ...DEFAULT_SETTINGS }
  private running = false
  private audioUnlocked = false
  private resultsShown = false
  private testMode = false
  private readonly hitStop = new HitStop()
  /** Set while this screen plays in a room on the game server. */
  private online: OnlinePlay | null = null

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    // The same fixed-step assembly the server and simcheck run; this class adds
    // the devices, the renderer and everything a person sees and hears.
    this.sim = createSimulation({ settings: this.settings })
    this.world = this.sim.world
    this.scheduler = this.sim.scheduler
    this.events = this.sim.events
    this.renderer = new Renderer(canvas, this.settings)

    this.world.setResource(SceneBridgeResource, this.renderer)
    this.world.setResource(ViewerResource, { slots: [0, 1], marked: null, ping: null })
    this.renderer.buildArena(ARENAS[0])

    // The fighter models. Nothing offers a fighter before they arrive: the menu
    // and a `#room=` link wait for them, or the picker and the first round
    // would show the procedural rigs for as long as the download takes —
    // seconds over the network. A model that fails to load falls back to its
    // rig, which the portraits then show too.
    const loading = el('div', 'loading')
    loading.textContent = 'ЗАГРУЗКА'
    uiRoot.append(loading)
    // A count that moves: on a slow line a still word reads as a hang.
    const progress = (bytes: number): void => {
      loading.textContent = `ЗАГРУЗКА · ${(bytes / 1e6).toFixed(1)} МБ`
    }
    void loadFighterModels(this.renderer.assets, progress).then((problems) => {
      for (const problem of problems) console.warn(`fighter model: ${problem}`)
      this.menu.renderPreviews(this.renderer.assets)
      loading.remove()
      this.menu.show('main')
      this.rooms.startup()
    })

    // ── ui ───────────────────────────────────────────────────────────────
    this.hud = new Hud(uiRoot)
    this.menu = new Menu(uiRoot, this.settings, {
      onStart: (config) => this.beginMatch(config),
      onOnline: () => {
        this.menu.show('none')
        this.rooms.open()
      },
      onResume: () => this.setPaused(false),
      onRestart: () => {
        // Unpause *first*: `startRound` moves the phase to 'countdown', and
        // `setPaused(false)` bails out unless the phase is still 'paused' — so
        // the other order left the overlay up and the clock stopped.
        this.setPaused(false)
        startRound(this.world)
      },
      onQuitToMenu: () => this.toMenu(),
      onSettings: (patch) => this.applySettings(patch),
    })
    this.rooms = new RoomAccess(uiRoot, () => this.menu.portraits(), {
      play: (client) => this.playOnline(client),
      between: () => this.betweenMatches(),
      quit: () => this.leaveOnline(),
    })
    this.debugEl = el('div', 'debug')
    uiRoot.append(this.debugEl)

    this.registerSystems()
    this.input.attach()
    this.installGlobalKeys()
    this.installAudioUnlock()
  }

  // ──────────────────────────────────────────────────────────── lifecycle ──

  start(): void {
    if (this.running) return
    this.running = true
    requestAnimationFrame(this.frame)
  }

  private beginMatch(config: MatchConfig): void {
    const def = scaledArena(arenaById(config.arenaId), config.arenaScale)
    this.renderer.buildArena(def)
    this.world.resource(ArenaResource).reset(def)

    const match = this.world.resource(MatchResource)
    match.controllers = config.controllers
    match.difficulty = config.difficulty
    match.models = config.models
    match.arenaScale = config.arenaScale
    match.teams = config.teams

    // Tell the input layer which slots to drive. With a single human it merges
    // the keyboard and the gamepad onto that one slot.
    const humanSlots = config.controllers
      .map((c, slot) => (c === 'human' ? slot : -1))
      .filter((slot) => slot >= 0)
    this.input.setHumanSlots(humanSlots)
    this.world.setResource(ViewerResource, { slots: humanSlots, marked: null, ping: null })

    startMatch(this.world, config.arenaId, config.winsNeeded)
    this.menu.show('none')
    this.time.paused = false
    sfxUi(this.audio, true)
  }

  /**
   * Hands this screen to a room on the game server. From here on the local
   * world is a replica: nothing is simulated in this browser, the devices
   * feed one slot, and presentation draws what the server sends.
   */
  playOnline(client: NetClient): void {
    if (this.online) {
      // The same room over a new connection: draw from that one instead.
      this.online.playback.reset()
    } else {
      // Whatever was on this screen before goes.
      const match = this.world.resource(MatchResource)
      match.phase = 'menu'
      clearRound(this.world)
      match.players = [-1, -1]
    }
    this.menu.show('none')
    this.time.paused = false
    this.online = new OnlinePlay(client, this.world, this.renderer)
    // One slot per browser: the keyboard and the pad both drive it.
    this.input.setHumanSlots([0])
    this.world.setResource(ViewerResource, { slots: [], marked: null, ping: null })
  }

  /** Back to playing on this screen alone. */
  leaveOnline(): void {
    if (this.online) {
      this.online.playback.reset()
      this.online = null
      this.world.setResource(ViewerResource, { slots: [0, 1], marked: null, ping: null })
    }
    this.toMenu()
  }

  /** The room's match ended or was stopped: an empty arena behind the room screen. */
  private betweenMatches(): void {
    this.online?.playback.reset()
    this.world.resource(MatchResource).phase = 'menu'
  }

  private toMenu(): void {
    const match = this.world.resource(MatchResource)
    match.phase = 'menu'
    // Clear the fighters so the menu sits over an empty arena rather than two
    // frozen characters mid-stride.
    clearRound(this.world)
    match.players = [-1, -1]
    this.menu.show('main')
    this.time.paused = false
    sfxUi(this.audio)
  }

  private setPaused(paused: boolean): void {
    const match = this.world.resource(MatchResource)
    if (paused) {
      if (match.phase === 'menu' || match.phase === 'matchOver' || match.phase === 'paused') {
        return
      }
      match.resumePhase = match.phase
      match.phase = 'paused'
      this.menu.show('pause')
      this.time.paused = true
    } else {
      if (match.phase !== 'paused') return
      match.phase = match.resumePhase
      this.menu.show('none')
      this.time.paused = false
    }
    sfxUi(this.audio)
  }

  private applySettings(patch: Partial<Settings>): void {
    Object.assign(this.settings, patch)
    this.renderer.applySettings(this.settings)
    this.audio.applySettings(this.settings)
    this.debugEl.classList.toggle('visible', this.settings.showDebug)
    this.scheduler.setProfiling(this.settings.showDebug)
  }

  // ────────────────────────────────────────────────────────────── systems ──

  /** The fixed-step systems are registered by `createSimulation`. */
  private registerSystems(): void {
    this.scheduler
      // Only the device poll runs per rendered frame; the AI and the apply run
      // inside every fixed step, where the poll's presses are consumed.
      .add(Stage.Input, 'inputPoll', createInputPollSystem(this.input), 0)

      .add(Stage.Presentation, 'camera', createCameraSystem(this.renderer), 0)
      .add(Stage.Presentation, 'renderSync', createRenderSyncSystem(this.renderer), 10)
      .add(Stage.Presentation, 'vfx', createVfxSystem(this.renderer), 20)
      .add(Stage.Presentation, 'audio', createAudioSystem(this.audio, this.input), 30)
      .add(Stage.Presentation, 'hud', createHudSystem(this.hud, this.renderer), 40)
  }

  // ─────────────────────────────────────────────────────────── test hooks ──

  /**
   * Scripted control over the game, used by the headless screenshot harness.
   *
   * The visual half of this project cannot be checked by simulating numbers —
   * "too dark to see the edge" is a fact about pixels. This API lets a headless
   * browser pose an exact situation, advance it by an exact amount and grab a
   * frame, which is the only honest way to review lighting and readability
   * without a human looking at a monitor.
   */
  testApi(): TestApi {
    const world = this.world
    // One slot per possible fighter, matching what the real InputManager
    // provides — a brawl has more fighters than a duel.
    const frame = createInputFrame()

    // A held button (Brace) survives `clearPresses` by design, so starting a
    // new scenario has to wipe the frame outright — otherwise a hold set up for
    // one shot silently stays down for every shot after it.
    const resetFrame = (): void => {
      for (const player of frame.players) resetInput(player)
    }

    return {
      startArena: (arenaId, winsNeeded = 3, models) => {
        this.testMode = true
        resetFrame()
        this.beginMatch({
          arenaId,
          winsNeeded,
          controllers: ['human', 'human'],
          difficulty: 1,
          models: models ?? ['guard', 'beetle'],
          arenaScale: 1,
          teams: [0, 1],
        })
        world.setResource(InputResource, frame)
      },
      startBrawl: (arenaId, fighters, difficulty, scripted = false, models) => {
        this.testMode = true
        resetFrame()
        // A posed shot needs the bots to hold still and take `move` and `press`
        // from the script; everywhere else they think, exactly as in a match.
        this.scheduler.setEnabled('ai', !scripted)
        const kinds = CHARACTER_KINDS
        this.beginMatch({
          arenaId,
          winsNeeded: 3,
          controllers: Array.from({ length: fighters }, () => 'ai' as const),
          difficulty: difficulty as 0 | 1 | 2,
          models: models ?? Array.from({ length: fighters }, (_, i) => kinds[i % kinds.length]),
          arenaScale: 1,
          teams: Array.from({ length: fighters }, (_, i) => i),
        })
        world.setResource(InputResource, frame)
      },
      showMenu: () => {
        this.testMode = true
        this.toMenu()
      },
      markOwn: (player) => {
        world.resource(ViewerResource).marked = player
      },
      skipCountdown: () => {
        const match = world.resource(MatchResource)
        if (match.phase === 'countdown') match.phaseTimer = 0.001
        // Just enough steps to enter the fight and settle; every extra step is
        // a rendered frame the harness pays for.
        this.stepFixed(12)
      },
      place: (player, x, z, angle) => {
        const e = world.resource(MatchResource).players[player]
        if (!world.isAlive(e)) return
        const tr = world.read(e, Transform)
        tr.x = tr.prevX = x
        tr.z = tr.prevZ = z
        if (angle !== undefined) tr.angle = tr.prevAngle = angle
        const vel = world.read(e, Velocity)
        vel.x = 0
        vel.z = 0
      },
      setInstability: (player, value) => {
        const e = world.resource(MatchResource).players[player]
        if (!world.isAlive(e)) return
        world.read(e, Instability).value = value
      },
      grantBuff: (player, kind, seconds) => {
        const e = world.resource(MatchResource).players[player]
        if (!world.isAlive(e)) return
        const buff = world.add(e, Buff)
        if (kind === 'shield') buff.shield = seconds
        else buff.haste = seconds
      },
      setVelocity: (player, vx, vz) => {
        const e = world.resource(MatchResource).players[player]
        if (!world.isAlive(e)) return
        const vel = world.read(e, Velocity)
        vel.x = vx
        vel.z = vz
      },
      move: (player, x, z) => {
        frame.players[player].moveX = x
        frame.players[player].moveZ = z
        frame.players[player].moveMag = Math.min(1, Math.hypot(x, z))
      },
      press: (player, ability) => {
        const key = ABILITY_INPUT_KEYS[ability]
        if (!key) return
        frame.players[player][key].pressed = true
        frame.players[player][key].down = true
      },
      hold: (player, ability, held) => {
        const key = ABILITY_INPUT_KEYS[ability]
        if (!key) return
        frame.players[player][key].down = held
      },
      step: (seconds) => this.stepFixed(Math.round(seconds * 60)),
      spawnPickup: (kind, x, z) => {
        spawnPickup(world, kind, x, z)
      },
      setCollapse: (scale) => {
        const arena = world.resource(ArenaResource)
        arena.scale = scale
        arena.collapsing = scale < 1
      },
      settings: (patch) => this.applySettings(patch),
      info: () => {
        const match = world.resource(MatchResource)
        const arena = world.resource(ArenaResource)
        return {
          phase: match.phase,
          arena: arena.def.id,
          arenaName: arena.def.name,
          scale: arena.scale,
          entities: world.entityCount,
          // Both are leak detectors. A bolt fired point blank used to be created
          // and destroyed inside one fixed step, which the render sync never
          // saw and therefore never reaped — and each one left a light behind.
          objects: this.renderer.objectCount,
          lights: this.renderer.lightCount,
          score: [...match.score] as [number, number],
          online: this.rooms.status(),
        }
      },
    }
  }

  /** Runs whole fixed steps plus their presentation and a render, in order. */
  private stepFixed(steps: number): void {
    const arena = this.world.resource(ArenaResource)
    const extent = Math.max(arena.def.extentX, arena.def.extentZ)

    for (let i = 0; i < steps; i++) {
      const ctx: SystemContext = {
        world: this.world,
        dt: 1 / 60,
        alpha: 0,
        elapsed: this.time.elapsed,
      }

      const match = this.world.resource(MatchResource)
      if (match.phase !== 'menu' && match.phase !== 'paused') {
        stepSimulation(this.sim, this.time.elapsed)
      }
      this.scheduler.run(Stage.Presentation, ctx)
      this.syncScreens(match.phase)
      this.renderer.advance(1 / 60, extent)
      this.events.clear()
      this.time.elapsed += 1 / 60
    }

    // Only the final state gets composited — the harness screenshots that one.
    this.renderer.present(1 / 60, this.time.elapsed)
  }

  // ───────────────────────────────────────────────────────────────── loop ──

  private frame = (timestamp: number): void => {
    if (!this.running) return
    requestAnimationFrame(this.frame)

    // Under scripted control the harness owns the clock; the animation frame
    // must not sneak extra simulation in between two screenshots.
    if (this.testMode) return

    const steps = this.time.beginFrame(timestamp)
    if (this.online) {
      this.onlineFrame(this.online, timestamp)
      return
    }
    const match = this.world.resource(MatchResource)

    const ctx: SystemContext = {
      world: this.world,
      dt: this.time.frameDt,
      alpha: this.time.alpha,
      elapsed: this.time.elapsed,
    }

    this.scheduler.run(Stage.Input, ctx)

    if (match.phase !== 'menu' && match.phase !== 'paused') {
      for (let i = 0; i < steps; i++) stepSimulation(this.sim, this.time.elapsed)
    }

    this.scheduler.run(Stage.Presentation, ctx)
    this.syncScreens(match.phase)

    const arena = this.world.resource(ArenaResource)
    const extent = Math.max(arena.def.extentX, arena.def.extentZ)
    // Effects and the camera run on unscaled time. Hit stop is meant to freeze
    // the *fighters* for a beat while the sparks, the ring and the shake carry
    // on at full speed — feeding them the 0.12× clock instead made every impact
    // ripple for eight times as long as designed and read as mush.
    this.renderer.render(Math.max(this.time.rawDt, 1 / 240), this.time.elapsed, extent)

    if (this.settings.showDebug) this.updateDebug()

    this.time.timeScale = this.hitStop.update(this.world, this.time.rawDt)

    // One drain per rendered frame, after every consumer has seen the events.
    this.events.clear()

    if (this.input.frame.pausePressed) {
      const current = this.world.resource(MatchResource).phase
      if (current === 'paused') this.setPaused(false)
      else if (current !== 'menu' && current !== 'matchOver') this.setPaused(true)
    }
  }

  /**
   * A rendered frame online: poll, send, play the snapshots back, draw.
   *
   * No simulation step and no hit stop — the server's clock freezes for
   * everyone, and its snapshots already show it. The camera and effects run
   * on real time, as they do offline.
   */
  private onlineFrame(online: OnlinePlay, now: number): void {
    const ctx: SystemContext = {
      world: this.world,
      dt: this.time.rawDt,
      alpha: 0,
      elapsed: this.time.elapsed,
    }
    this.scheduler.run(Stage.Input, ctx)
    const step = online.frame(now, this.input.frame.players[0])
    // The presses went to the server; no local step will consume them.
    consumePresses(this.input.frame)

    const arena = this.world.resource(ArenaResource)
    if (step.newMatch) this.renderer.buildArena(arena.def)
    else if (step.newRound) this.renderer.resetArena()
    const viewer = this.world.resource(ViewerResource)
    viewer.slots = online.slot === null ? [] : [online.slot]
    viewer.marked = online.slot
    viewer.ping = online.client.rtt > 0 ? online.client.rtt : null

    this.scheduler.run(Stage.Presentation, { ...ctx, alpha: step.alpha })
    this.syncScreens(this.world.resource(MatchResource).phase)
    const extent = Math.max(arena.def.extentX, arena.def.extentZ)
    this.renderer.render(Math.max(this.time.rawDt, 1 / 240), this.time.elapsed, extent)
    if (this.settings.showDebug) this.updateDebug()
    this.events.clear()

    // No pause online: Esc opens a menu over a match that keeps going.
    if (this.input.frame.pausePressed) this.rooms.toggleMatchMenu()
  }

  private syncScreens(phase: string): void {
    if (phase !== 'matchOver') {
      this.resultsShown = false
      return
    }
    const match = this.world.resource(MatchResource)
    if (this.resultsShown || match.matchWinner === null) return
    this.resultsShown = true
    const winner = teamName(match, match.matchWinner)
    if (this.online) this.rooms.showResults(winner, match.score)
    else this.menu.showResults(winner, match.score)
  }

  private updateDebug(): void {
    const match = this.world.resource(MatchResource)
    const arena = this.world.resource(ArenaResource)
    const lines = [
      `FPS        ${this.time.fps.toFixed(0)}`,
      `STEPS      ${this.time.stepsLastFrame}`,
      `ENTITIES   ${this.world.entityCount}`,
      `PHASE      ${match.phase}`,
      `ROUND T    ${match.roundTime.toFixed(1)}`,
      `ARENA S    ${arena.scale.toFixed(3)}`,
      '',
      ...this.scheduler
        .timings()
        .filter((t) => t.ms > 0.05)
        .map((t) => `${t.name.padEnd(11)}${t.ms.toFixed(2)}ms`),
    ]
    this.debugEl.textContent = lines.join('\n')
  }

  // ─────────────────────────────────────────────────────────────── input ──

  private installGlobalKeys(): void {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'F3') {
        e.preventDefault()
        this.applySettings({ showDebug: !this.settings.showDebug })
      }
    })
  }

  /**
   * Browsers refuse to start an AudioContext without a user gesture, so the
   * first key press or click in the page is what actually brings the sound up.
   */
  private installAudioUnlock(): void {
    const unlock = (): void => {
      if (this.audioUnlocked) return
      this.audioUnlocked = true
      void this.audio.resume().then(() => this.audio.applySettings(this.settings))
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
  }
}

// ──────────────────────────────────────────────────────────────── test API ──

export interface TestApi {
  startArena(
    arenaId: string,
    winsNeeded?: number,
    models?: [CharacterKind, CharacterKind],
  ): void
  /**
   * Starts an all-bot free-for-all with the given number of fighters. With
   * `scripted` the bots do not think and take `move`/`press` like humans.
   */
  startBrawl(
    arenaId: string,
    fighters: number,
    difficulty: number,
    scripted?: boolean,
    models?: CharacterKind[],
  ): void
  /** Marks a fighter as this screen's own, as online play does; null clears it. */
  markOwn(player: PlayerId | null): void
  showMenu(): void
  /** Ends the countdown immediately and settles into the fight. */
  skipCountdown(): void
  place(player: PlayerId, x: number, z: number, angle?: number): void
  setInstability(player: PlayerId, value: number): void
  /** Grants a pickup effect directly, without waiting for one to spawn. */
  grantBuff(player: PlayerId, kind: PickupKind, seconds: number): void
  /** Injects velocity, to pose a fighter mid-flight. */
  setVelocity(player: PlayerId, vx: number, vz: number): void
  move(player: PlayerId, x: number, z: number): void
  press(player: PlayerId, ability: number): void
  hold(player: PlayerId, ability: number, held: boolean): void
  /** Advances the simulation by whole fixed steps and renders each one. */
  step(seconds: number): void
  /** Places a pickup directly, without waiting for the spawn timer. */
  spawnPickup(kind: PickupKind, x: number, z: number): void
  /** Forces the arena to a collapse scale without waiting 25 s for it. */
  setCollapse(scale: number): void
  settings(patch: Partial<Settings>): void
  info(): {
    phase: string
    arena: string
    arenaName: string
    scale: number
    entities: number
    objects: number
    lights: number
    score: [number, number]
    /** This browser's place in a room, or null offline. */
    online: { host: boolean; playing: boolean; screen: string } | null
  }
}

const ABILITY_INPUT_KEYS: Record<number, 'push' | 'dash' | 'wave' | 'brace' | 'vortex'> = {
  [AbilityId.Push]: 'push',
  [AbilityId.Dash]: 'dash',
  [AbilityId.Wave]: 'wave',
  [AbilityId.Brace]: 'brace',
  [AbilityId.Vortex]: 'vortex',
}
